import { useState, useRef, useEffect } from "react";
import type { EventCategory } from "@/types";
import type { Event } from "@/types";
import { IconButton, SecondaryButton } from "@components/atoms";
import {
  EventCardSecondary,
  DateRangePicker,
  PriceRangeSlider,
  CategorySelector,
} from "@components/molecules";
import { FilterModal } from "@components/organisms";
import { Filter, X } from "lucide-react";
import { cn } from "@shared/utils";

interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface PriceRange {
  min: number;
  max: number;
}

interface EventGridProps {
  events: Event[];
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  priceRange: PriceRange;
  onPriceRangeChange: (range: PriceRange) => void;
  selectedCategories: EventCategory[];
  onCategoriesChange: (categories: EventCategory[]) => void;
  onClearAll: () => void;
}

/**
 * Event grid organism.
 * Displays events in a 5-column grid with filter buttons and dropdowns.
 * Integrated with useEventFilters hook via props.
 */
export function EventGrid({
  events,
  dateRange,
  onDateRangeChange,
  priceRange,
  onPriceRangeChange,
  selectedCategories,
  onCategoriesChange,
  onClearAll,
}: EventGridProps) {
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<
    "date" | "price" | "category" | null
  >(null);
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [tempPriceRange, setTempPriceRange] = useState(priceRange);
  const [tempCategories, setTempCategories] = useState(selectedCategories);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync temporary states when filter props change
  useEffect(() => {
    setTempDateRange(dateRange);
  }, [dateRange]);

  useEffect(() => {
    setTempPriceRange(priceRange);
  }, [priceRange]);

  useEffect(() => {
    setTempCategories(selectedCategories);
  }, [selectedCategories]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openDropdown]);

  const handleDateClear = () => {
    const empty = { start: null, end: null };
    setTempDateRange(empty);
    onDateRangeChange(empty); // ← actualiza el hook
    setOpenDropdown(null);
  };

  const handleDateApply = () => {
    onDateRangeChange(tempDateRange);
    setOpenDropdown(null);
  };

  const handlePriceClear = () => {
    const empty = { min: 0, max: 300 };
    setTempPriceRange(empty);
    onPriceRangeChange(empty);
    setOpenDropdown(null);
  };

  const handlePriceApply = () => {
    onPriceRangeChange(tempPriceRange);
    setOpenDropdown(null);
  };

  const handleCategoryClear = () => {
    setTempCategories([]);
    onCategoriesChange([]);
    setOpenDropdown(null);
  };

  const handleCategoryApply = () => {
    onCategoriesChange(tempCategories);
    setOpenDropdown(null);
  };

  const hasActiveFilters =
    dateRange.start !== null ||
    dateRange.end !== null ||
    priceRange.min > 0 ||
    priceRange.max < 300 ||
    selectedCategories.length > 0;

  return (
    <>
      <section className="w-full px-4 sm:px-8 md:px-16 lg:px-20 py-8 sm:py-12">
        {/* Filter bar */}
        <div className="mx-auto w-full">
          <div className="mb-6 flex items-center gap-[9px] flex-wrap">
            {/* Filter modal trigger */}
            <IconButton
              size={24}
              onClick={() => setIsFilterModalOpen(true)}
              className={cn(
                "transition-colors",
                hasActiveFilters ? "bg-secondary-120/20" : "",
              )}
            >
              <Filter
                size={24}
                className={cn(
                  "transition-colors",
                  hasActiveFilters
                    ? "text-secondary-120"
                    : "text-secondary-120",
                )}
              />
            </IconButton>

            {/* Date dropdown trigger */}
            <div className="relative">
              <SecondaryButton
                withChevron
                isActive={!!(dateRange.start || dateRange.end)}
                onClick={() =>
                  setOpenDropdown(openDropdown === "date" ? null : "date")
                }
              >
                Fecha
              </SecondaryButton>

              {openDropdown === "date" && (
                <div ref={dropdownRef}>
                  <DateRangePicker
                    value={tempDateRange}
                    onChange={(range) => {
                      setTempDateRange(range);
                      onDateRangeChange(range);
                    }}
                    onClear={handleDateClear}
                    onApply={handleDateApply}
                    onClose={() => setOpenDropdown(null)}
                  />
                </div>
              )}
            </div>

            {/* Price dropdown trigger */}
            <div className="relative">
              <SecondaryButton
                withChevron
                isActive={priceRange.min > 0 || priceRange.max < 300}
                onClick={() =>
                  setOpenDropdown(openDropdown === "price" ? null : "price")
                }
              >
                Precio
              </SecondaryButton>

              {openDropdown === "price" && (
                <div
                  ref={dropdownRef}
                  className="absolute top-full left-0 z-20 mt-2 bg-white rounded-2xl shadow-lg p-4 w-80"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-primary-110 font-semibold">Precio</h4>
                    <button
                      onClick={() => setOpenDropdown(null)}
                      className="p-1 hover:bg-neutral-10 rounded transition-colors"
                    >
                      <X
                        size={20}
                        className="text-primary-110 cursor-pointer"
                      />
                    </button>
                  </div>
                  <PriceRangeSlider
                    value={tempPriceRange}
                    onChange={(range) => {
                      setTempPriceRange(range);
                      onPriceRangeChange(range);
                    }}
                    onClear={handlePriceClear}
                    onApply={handlePriceApply}
                    hideTitle={true}
                  />
                </div>
              )}
            </div>

            {/* Category dropdown trigger */}
            <div className="relative">
              <SecondaryButton
                withChevron
                isActive={selectedCategories.length > 0}
                onClick={() =>
                  setOpenDropdown(
                    openDropdown === "category" ? null : "category",
                  )
                }
              >
                Categoría
              </SecondaryButton>
              {openDropdown === "category" && (
                <div
                  ref={dropdownRef}
                  className="absolute top-full left-0 z-20 mt-2 bg-white rounded-2xl shadow-lg p-4 w-80"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-primary-110 font-semibold">
                      Categoría
                    </h4>
                    <button
                      onClick={() => setOpenDropdown(null)}
                      className="p-1 hover:bg-neutral-10 rounded transition-colors"
                    >
                      <X
                        size={20}
                        className="text-primary-110 cursor-pointer"
                      />
                    </button>
                  </div>
                  <CategorySelector
                    value={tempCategories}
                    onChange={(cats) => {
                      setTempCategories(cats);
                      onCategoriesChange(cats);
                    }}
                    onClear={handleCategoryClear}
                    onApply={handleCategoryApply}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Grid of secondary cards — responsive columns */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8 mb-8">
            {events.map((event) => (
              <EventCardSecondary key={event.id} event={event} />
            ))}
          </div>
        </div>

        {events.length === 0 && (
          <p className="py-12 text-center text-white/60 text-body">
            No se encontraron eventos con los filtros seleccionados.
          </p>
        )}
      </section>

      {/* Filter Modal */}
      <FilterModal
        key={isFilterModalOpen ? "open" : "closed"}
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        priceRange={priceRange}
        onPriceRangeChange={onPriceRangeChange}
        selectedCategories={selectedCategories}
        onCategoriesChange={onCategoriesChange}
        onClearAll={onClearAll}
      />
    </>
  );
}
